import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../../services/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { UserAccountService } from '../../../../services/user-account/user-account.service';
import { PushNotificationsService } from '../../../../services/push-notifications/push-notifications.service';

@Component({
  selector: 'app-two-fa',
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './two-fa.component.html',
  styleUrl: './two-fa.component.css'
})
export class TwoFaComponent implements OnInit {
  otpForm: FormGroup;
  timer: number = 60;
  interval: any;
  email: string = '';
  loginDetails: any;
  ipDetails: any;
  subscriptionDetails: any;
  accessDetails: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private pushNotificationsService: PushNotificationsService,
  ) {
    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp2: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp3: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp4: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp5: ['', [Validators.required, Validators.pattern('[0-9]')]],
      otp6: ['', [Validators.required, Validators.pattern('[0-9]')]],
    });
  }

  ngOnInit(): void {
    this.startTimer();

    let emailFromStorage = localStorage.getItem('email');
    if (emailFromStorage) {
      this.email = emailFromStorage;
    } else {
      this.toastr.error('Email not found. Please try again.', 'Error');
      this.router.navigate(['/login']);
    }
    //this.getIpDetails();
  }


  getIpDetails() {
    this.authService.getIpDetails().subscribe({
      next: (response) => {
        this.ipDetails = response;
        this.storeIpDetails();
      },
      error: (error) => {
        console.error(error);
      },
    });

  }

  updateIpDetails() {
    this.authService.getIpDetailsWithFallback().subscribe({
      next: (response) => {
        this.ipDetails = response;
        this.storeIpDetails();
        this.userAccountService.sendTrackingInfo(this.getTrackingPayload()).subscribe((data) => { });
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  startTimer(): void {
    this.interval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        clearInterval(this.interval);
      }
    }, 1000);
  }

  moveToNext(event: any, nextInput: string): void {
    if (event.target.value.length === 1) {
      const nextElement = document.getElementById(nextInput);
      if (nextElement) {
        nextElement.focus();
      }
    }
  }

  // Handle backspace/delete key
  onKeyDown(event: KeyboardEvent, currentInput: string, previousInput: string | null): void {
    const target = event.target as HTMLInputElement;

    // If backspace is pressed and current input is empty, move to previous input
    if (event.key === 'Backspace' && target.value === '' && previousInput) {
      event.preventDefault();
      const prevElement = document.getElementById(previousInput) as HTMLInputElement;
      if (prevElement) {
        prevElement.focus();
        // Optionally clear the previous input
        const controlName = previousInput.replace('otp', 'otp');
        this.otpForm.get(controlName)?.setValue('');
      }
    }
  }

  onSubmit(): void {
    if (this.otpForm.valid) {
      const otp = `${this.otpForm.value.otp1}${this.otpForm.value.otp2}${this.otpForm.value.otp3}${this.otpForm.value.otp4}${this.otpForm.value.otp5}${this.otpForm.value.otp6}`;
      this.authService.verify2Fa({ login: this.email, token: otp }).subscribe({
        next: (response: any) => {
          this.toastr.success('Signin successful');
          this.loginDetails = response?.data;
          this.storeLoginDetails();
          this.getAccessPermissions();

          if (response.data.enable_ip_tracking) {
            this.updateIpDetails();
          }
        },
        error: () => {
          this.toastr.error('Invalid OTP. Please try again.', 'Error');
        },
      });
    }
  }

  getTrackingPayload() {
    const locationParts = [
      this.ipDetails?.city,
      this.ipDetails?.region,
      this.ipDetails?.country,
    ];
    const location = locationParts.filter(Boolean).join(', ');
    return {
      location: location || undefined,
      ip_address: this.ipDetails?.ip,
      device: this.getDeviceDetails(),
    };
  }

  getDeviceDetails() {
    return navigator.userAgent;
  }

  getAccessPermissions() {
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};

        this.subscriptionDetails = subscription;
        this.storeSubscriptionDetails();

        this.accessDetails = {
          face_search_permission: subscription?.face_search_permission,
          face_compare_permission: subscription?.face_compare_permission,
          video_scanner_permission: subscription?.video_scanner_permission,
          osint_search_permission: subscription?.osint_search_permission,
          leak_search_permission: subscription?.leak_search_permission,
          pan_search_permission: subscription?.pan_search_permission,
          gst_search_permission: subscription?.gst_search_permission,
          din_search_permission: subscription?.din_search_permission,
          mobile_pan_search_permission: subscription?.mobile_pan_search_permission,
          phonepe_fastag_search_permission: subscription?.phonepe_fastag_search_permission,
          phonepe_upi_search_permission: subscription?.phonepe_upi_search_permission,
          vehicle_search_permission: subscription?.vehicle_search_permission,
        };
        this.storeAccessDetails();

        // Ask for browser notification permission right after 2FA login.
        this.pushNotificationsService.requestPushAfterLogin();

        if (subscription?.osint_search_permission) {
          this.router.navigate(['/osint-number-search']);
        } else if (subscription?.face_search_permission) {
          this.router.navigate(['/osint-image-search']);
        } else if (subscription?.face_compare_permission) {
          this.router.navigate(['/osint-image-search/face-compare']);
        } else if (subscription?.video_scanner_permission) {
          this.router.navigate(['/osint-image-search/video-scanner']);
        } else if (subscription?.leak_search_permission) {
          this.router.navigate(['/leak-number-search']);
        } else {
          this.router.navigate(['/user-account']);
        }
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => { },
    });
  }

  storeIpDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.ipDetails = this.ipDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  storeLoginDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.loginDetails = this.loginDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  storeAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.accessDetails = this.accessDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  storeSubscriptionDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.subscriptionDetails = this.subscriptionDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }
}